import LoggedInApp from '@/layouts/LoggedInApp.vue'
import LoginPage from '@/modules/auth/views/LoginPage.vue'
import { i18n } from '@/plugins/i18n'
import { useVersionUpdateStore } from '@/stores/versionUpdate'
import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: LoginPage,
      meta: { requiresAuth: false },
    },
    {
      path: '/',
      component: LoggedInApp,
      meta: { requiresAuth: true },
      children: [
        { path: '', redirect: '/dashboard' },
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import('@/modules/dashboard/views/DashboardPage.vue'),
          meta: { title: () => i18n.global.t('modules.dashboard.title') },
        },
        {
          path: 'organizations',
          name: 'organizations',
          component: () => import('@/modules/organizations/views/OrganizationsPage.vue'),
          meta: { title: () => i18n.global.t('modules.organizations.title', 2) },
        },
        {
          path: 'organizations/new',
          name: 'new-organization',
          component: () => import('@/modules/organizations/views/NewOrganizationPage.vue'),
        },
        {
          path: 'organizations/:id/edit',
          name: 'edit-organization',
          component: () => import('@/modules/organizations/views/EditOrganizationPage.vue'),
        },
        {
          path: 'organizations/:id',
          name: 'view-organization',
          component: () => import('@/modules/organizations/views/ViewOrganizationPage.vue'),
        },
        {
          path: 'organizations/:id/users/new',
          name: 'new-user',
          component: () => import('@/modules/users/views/NewUserPage.vue'),
        },
        {
          path: 'organizations/groups',
          name: 'organizations-groups',
          component: () => import('@/modules/organization-access/views/OrganizationGroupsPage.vue'),
        },
        {
          path: 'organizations/:id/groups',
          name: 'groups',
          component: () => import('@/modules/organization-access/views/GroupsPage.vue'),
        },
        {
          path: 'users',
          name: 'users',
          component: () => import('@/modules/users/views/UsersPage.vue'),
          meta: { title: () => i18n.global.t('modules.users.title', 2) },
        },
        {
          path: 'plans',
          name: 'plans',
          component: () => import('@/modules/plans/views/PlansPage.vue'),
          meta: { title: () => i18n.global.t('modules.plans.title', 2) },
        },
        {
          path: 'plans/new',
          name: 'new-plan',
          component: () => import('@/modules/plans/views/NewPlanPage.vue'),
        },
        {
          path: 'plans/:code/edit',
          name: 'edit-plan',
          component: () => import('@/modules/plans/views/EditPlanPage.vue'),
        },
        {
          path: 'plans/:code',
          name: 'view-plan',
          component: () => import('@/modules/plans/views/ViewPlanPage.vue'),
        },
        {
          path: 'users/:id',
          name: 'view-user',
          component: () => import('@/modules/users/views/ViewUserPage.vue'),
        },
        {
          path: 'users/:id/edit',
          name: 'edit-user',
          component: () => import('@/modules/users/views/EditUserPage.vue'),
        },
        {
          path: 'llm-usage',
          name: 'llm-usage',
          component: () => import('@/modules/llm-usage/views/LLMSearchPage.vue'),
          meta: { title: () => i18n.global.t('modules.llm_usage.title', 2) },
        },
        {
          path: 'feedback',
          name: 'feedback',
          component: () => import('@/modules/feedback/views/FeedbackPage.vue'),
          meta: {
            title: () => i18n.global.t('modules.feedback.title'),
            requiredOperation: 'admin_feedback_read',
          },
        },
        {
          path: 'feedback/message/:id',
          name: 'view-message-feedback',
          component: () => import('@/modules/feedback/views/ViewMessageFeedbackPage.vue'),
          meta: {
            title: () => i18n.global.t('modules.feedback.detail.title'),
            requiredOperation: 'admin_feedback_read',
          },
        },
        {
          path: 'redis',
          name: 'redis',
          component: () => import('@/modules/redis/views/RedisHomePage.vue'),
          meta: { title: () => i18n.global.t('modules.redis.title', 2) },
        },
        {
          path: 'maintenance',
          name: 'maintenance',
          component: () => import('@/modules/maintenance/views/MaintenancePage.vue'),
          meta: { title: () => i18n.global.t('modules.maintenance.title') },
        },
        {
          path: 'admin-users',
          name: 'admin-users',
          component: () => import('@/modules/admin-users/views/AdminUsersPage.vue'),
          meta: { title: () => i18n.global.t('modules.admin_users.title', 2) },
        },
        {
          path: 'admin-users/:id/permissions',
          name: 'admin-user-permissions',
          component: () => import('@/modules/admin-users/views/AdminUserPermissionsPage.vue'),
          meta: { title: () => i18n.global.t('modules.admin_users.permissions.title') },
        },
        {
          path: 'permission-templates',
          name: 'permission-templates',
          component: () =>
            import('@/modules/permission-templates/views/PermissionTemplatesPage.vue'),
          meta: {
            title: () => i18n.global.t('modules.permission_templates.title', 2),
            requiredOperation: 'admin_permission_template_read',
          },
        },
        {
          path: 'permission-templates/compare',
          name: 'compare-permission-templates',
          component: () => import('@/modules/permission-templates/views/CompareTemplatesPage.vue'),
          meta: {
            title: () => i18n.global.t('modules.permission_templates.compare.title'),
            requiredOperation: 'admin_permission_template_read',
          },
        },
        {
          path: 'permission-templates/new',
          name: 'new-permission-template',
          component: () => import('@/modules/permission-templates/views/NewTemplatePage.vue'),
          meta: { requiredOperation: 'admin_permission_template_create' },
        },
        {
          path: 'permission-templates/:id',
          name: 'view-permission-template',
          component: () => import('@/modules/permission-templates/views/ViewTemplatePage.vue'),
          meta: { requiredOperation: 'admin_permission_template_read' },
        },
        {
          path: 'billing/credit-pricing',
          name: 'credit-pricing',
          component: () => import('@/modules/billing/views/CreditPricingPage.vue'),
          meta: {
            title: () => i18n.global.t('modules.billing.creditPricing.title'),
            requiredOperation: 'admin_billing_read',
          },
        },
        {
          path: 'onboarding/email-templates',
          name: 'onboarding-email-templates',
          component: () => import('@/modules/onboarding/views/EmailTemplatesPage.vue'),
          meta: {
            title: () => i18n.global.t('modules.onboarding.emailTemplates.title'),
            requiredOperation: 'admin_orgs_read',
          },
        },
        {
          path: 'modules',
          name: 'modules',
          component: () => import('@/modules/features/views/ModulesPage.vue'),
          meta: { title: () => i18n.global.t('modules.module.title', 2) },
        },
        {
          path: 'modules/:id',
          name: 'view-module',
          component: () => import('@/modules/features/views/ViewModulePage.vue'),
        },
        {
          path: 'help',
          name: 'help',
          component: () => import('@/modules/help/views/HelpPage.vue'),
          meta: { title: () => i18n.global.t('modules.help.title') },
        },
        {
          path: 'build',
          name: 'build-info',
          component: () => import('@/modules/build/BuildInfoPage.vue'),
          meta: { title: 'Build Information' },
        },
        {
          path: 'dev',
          name: 'dev',
          component: () => import('@/modules/dev/views/DevPage.vue'),
          meta: { title: 'Dev' },
        },
      ],
    },
  ],
  scrollBehavior(to) {
    if (to.hash) return { el: to.hash }
  },
})

// Navigation guards

/**
 * Forces a full-page reload when a new app version has been detected,
 * so the next route renders the freshly-loaded JS bundle. The actual
 * "Updated to vX.Y.Z" toast is shown post-reload by reading the
 * sessionStorage markers written by `markPendingReload()`.
 */
export function versionUpdateGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): boolean {
  // Skip initial navigation - Vue Router 4 leaves from.name undefined on first hit.
  if (from.name === undefined) return true
  // Skip same-route navigation - no visual cover for a reload.
  if (to.fullPath === from.fullPath) return true

  const store = useVersionUpdateStore()
  if (!store.hasNewVersion) return true

  try {
    store.markPendingReload()
  } catch (error) {
    console.warn('markPendingReload failed', error)
  }
  window.location.assign(to.fullPath)
  return false
}

router.beforeEach(versionUpdateGuard)

router.beforeEach(async (to, _from, next) => {
  const { useAuthStore } = await import('@/stores/auth')
  const authStore = useAuthStore()

  // Initialize auth store and wait for completion
  await authStore.initialize()

  const requiresAuth = to.meta.requiresAuth !== false
  const isAuthenticated = authStore.isAuthenticated

  if (requiresAuth && !isAuthenticated) {
    // Redirect to login if authentication is required but user is not authenticated
    next({ name: 'login', query: { redirect: to.fullPath } })
  } else if (to.name === 'login' && isAuthenticated) {
    // Redirect to dashboard if user is already authenticated and trying to access login
    const redirect = (to.query.redirect as string) || '/dashboard'
    next(redirect)
  } else {
    // Operation-level gating: mirror the backend's RequireAdminOperations boundary.
    // A route can declare meta.requiredOperation; users lacking it are bounced to
    // the dashboard (the same operation also hides the sidebar entry).
    const requiredOperation = to.meta.requiredOperation as string | undefined
    if (requiredOperation && isAuthenticated && !authStore.hasPermission(requiredOperation)) {
      next({ name: 'dashboard' })
    } else {
      next()
    }
  }
})

export default router
